/**
 * Role constants for the application.
 * These roles are assigned by the backend and stored in the user profile.
 */

/** Standard user role — grants access to the main app (chat, user-facts, favorites, etc.) */
export const USER_ROLE = 'user' as const;

export const ADMIN_ROLES = {
  /** Access to HR Reports page */
  REPORTS_READER: 'reportsReader',
  /** Access to Session Analysis (Analyze) page */
  SESSIONS_READER: 'sessionsReader',
  /** Access to Test Evaluation pages */
  TEST_CONTRIBUTOR: 'testContributor',
  /** Access to feedback data (used in Analyze page) */
  FEEDBACKS_READER: 'feedbacksReader',
  /** General admin role (currently unused but defined) */
  ADMIN: 'admin',
  /** Cross-tenant access to all reports */
  GLOBAL_ADMIN: 'globalAdmin',
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

/**
 * All admin portal roles - used to check if user has any admin access
 */
export const ALL_ADMIN_PORTAL_ROLES: AdminRole[] = [
  ADMIN_ROLES.REPORTS_READER,
  ADMIN_ROLES.SESSIONS_READER,
  ADMIN_ROLES.TEST_CONTRIBUTOR,
  ADMIN_ROLES.GLOBAL_ADMIN,
];

/**
 * Check if user has any admin portal role
 */
export const hasAnyAdminRole = (roles: string[] | undefined): boolean => {
  if (!roles) return false;
  return ALL_ADMIN_PORTAL_ROLES.some(role => roles.includes(role));
};

/**
 * Check if user has a specific role
 */
export const hasRole = (roles: string[] | undefined, role: string): boolean => {
  if (!roles) return false;
  return roles.includes(role);
};

/**
 * Check if user has minimum access to enter the admin portal (any admin role).
 */
export const hasMinimumAdminAccess = (roles: string[] | undefined): boolean => {
  return hasAnyAdminRole(roles);
};

/**
 * Admin-only user: has admin portal roles but NOT the 'user' role.
 * These users are restricted to /admin routes — main app routes are disabled.
 */
export const isAdminOnlyUser = (roles: string[] | undefined): boolean => {
  if (!roles) return false;
  return hasAnyAdminRole(roles) && !roles.includes(USER_ROLE);
};

/**
 * User has access to the main app (chat, user-facts, favorites, etc.).
 * True for default users (no admin roles) and for admin+user users.
 * False for admin-only users.
 */
export const hasMainAppAccess = (roles: string[] | undefined): boolean => {
  return !isAdminOnlyUser(roles);
};

/**
 * Get the default admin route based on user roles.
 * Returns the first available admin page path.
 */
export const getDefaultAdminRoute = (roles: string[] | undefined): string => {
  const pages = getAvailableAdminPages(roles);
  return pages.length > 0 ? pages[0].href : '/admin/reports';
};

/**
 * Get available admin pages based on user roles
 */
export interface AdminNavItem {
  name: string;
  href: string;
  role: AdminRole;
}

export const getAvailableAdminPages = (roles: string[] | undefined): AdminNavItem[] => {
  const pages: AdminNavItem[] = [];
  const isGlobalAdmin = hasRole(roles, ADMIN_ROLES.GLOBAL_ADMIN);
  
  if (isGlobalAdmin || hasRole(roles, ADMIN_ROLES.REPORTS_READER)) {
    pages.push({ name: 'Reports', href: '/admin/reports', role: ADMIN_ROLES.REPORTS_READER });
  }
  
  if (isGlobalAdmin || hasRole(roles, ADMIN_ROLES.SESSIONS_READER)) {
    pages.push({ name: 'Analyze', href: '/admin/analyze', role: ADMIN_ROLES.SESSIONS_READER });
  }
  
  if (isGlobalAdmin || hasRole(roles, ADMIN_ROLES.TEST_CONTRIBUTOR)) {
    pages.push({ name: 'Test Evaluation', href: '/admin/test-evaluation', role: ADMIN_ROLES.TEST_CONTRIBUTOR });
  }
  
  return pages;
};
